/**
 * 10a_bhramari.tsx
 * Bhramari Pranayama (humming bee breath) — sits between Chandra Bhedana and
 * Meditation. Default export is the tutorial slide; `BhramariBody` is the
 * PracticeSwiper version (no separate _body copy).
 *
 * Guide timeline: optional intro clip → 5 rounds (inhale 4 · hum 10) →
 * dispatches `yogyism:next` (straight on to Meditation, like the other practices).
 *
 * A soft background drone (same as the other pranayama slides) plays
 * throughout. Narration is optional: if /audio/bhramari-practice.mp3 (ONE
 * round, looped) loads, the visuals follow its currentTime. If it is missing
 * or blocked, the guide paces itself at 1 s per count and adds Web Audio cues
 * (a chime on each inhale, a low guide-hum on each exhale).
 */
import React, { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import { useMode } from "./modeBus";
import HTLDSlide10aMobile from "./HTLDSlide10aMobile";
import {
  accent10a as accent, accent10aRgb as RGB, tint10a as tint,
  slide10aContent, useSlide10a, Seg,
  BHRAMARI_INHALE, BHRAMARI_HUM, BHRAMARI_ROUNDS, BHRAMARI_EXHALE_AT,
} from "./slide10a.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { getLang, pick } from "i18n/lang";
import { audioUrl, localAudio } from "i18n/audio";

const font = FONT_BODY;
const rgba = (alpha: number) => `rgba(${RGB},${alpha})`;
const glow = (alpha: number) => `rgba(${tint.glowRgb},${alpha})`;

if (typeof document !== "undefined" && !document.getElementById("yogyism-pulse-bhramari")) {
  const s = document.createElement("style");
  s.id = "yogyism-pulse-bhramari";
  s.textContent = `
    @keyframes yogyismPulse_bhramari {
      0%   { box-shadow: 0 0 0 0 ${rgba(0.70)}, 0 0 12px 2px ${rgba(0.45)}; transform: scale(1); }
      50%  { box-shadow: 0 0 0 16px ${rgba(0)}, 0 0 20px 6px ${rgba(0.65)}; transform: scale(1.12); }
      100% { box-shadow: 0 0 0 0 ${rgba(0)}, 0 0 12px 2px ${rgba(0.45)}; transform: scale(1); }
    }`;
  document.head.appendChild(s);
}

const S: Record<string, CSSProperties> = {
  root: {
    background: "#040f18", fontFamily: font, color: "#d0e8f8",
    width: "100%", height: "100%",
    display: "flex", flexDirection: "column",
    boxSizing: "border-box", overflow: "hidden",
    position: "relative",
  },
  subtitleBar: {
    background: "#050f1a", borderBottom: `1px solid ${rgba(0.12)}`,
    padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: "clamp(12px,2vw,28px)", flexShrink: 0,
  },
  body: {
    flex: 1, minHeight: 0,
    display: "flex", gap: "clamp(16px,2vw,28px)",
    padding: "clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)",
    alignItems: "stretch", overflow: "hidden",
  },
  content: {
    flex: 1, minWidth: "min(100%, 380px)",
    display: "flex", flexDirection: "column",
    justifyContent: "space-between",
  },
  panel: {
    width: "clamp(220px,28vw,380px)", flexShrink: 0,
    display: "flex", flexDirection: "column", alignItems: "center",
    overflow: "hidden",
  },
};

// ─── Guide timing ─────────────────────────────────────────────────────────────
const ROUND_COUNTS = BHRAMARI_INHALE + BHRAMARI_HUM; // 14
const COUNT_SECS   = 1;                               // pacing without narration
const TIMER_CYCLE  = ROUND_COUNTS * COUNT_SECS;
const INTRO_SRC    = "/audio/intro-bhramari.mp3";
const PRACTICE_SRC = "/audio/bhramari-practice.mp3";
const HUM_FADE_SECS = 0.8; // soft fade at the end of each round (the recording stops abruptly)

type Stage = "idle" | "intro" | "practice";

type View =
  | { stage: "idle" }
  | { stage: "intro"; cosmetic: number }
  | { stage: "practice"; tInRound: number; cycle: number; round: number; cosmetic: number; exhaleAt?: number | null };

// ─── Canvas drawing (pure: depends only on the View) ─────────────────────────
const W = 400, H = 560;
const cx = 200, circCY = 220;
const R_MIN = 42, R_MAX = 90;
const STARS = [[20,18,0.9],[72,10,0.7],[310,16,1.0],[370,40,0.7],[35,65,0.8],[350,60,0.9],[385,8,0.9],[110,42,0.7]];

function easeInOut(t: number) { return t < 0.5 ? 2*t*t : -1+(4-2*t)*t; }

// exhaleAt (s): the recording's measured "Exhale" moment. Inhale fills
// [0, exhaleAt), hum fills [exhaleAt, cycle). Without it, split by counts.
function phaseAt(tInRound: number, cycle: number, exhaleAt?: number | null) {
  if (exhaleAt && exhaleAt > 0 && exhaleAt < cycle) {
    const t = Math.min(cycle, Math.max(0, tInRound));
    if (t < exhaleAt) {
      const p = t / exhaleAt;
      return { hum: false, progress: p, count: Math.max(1, Math.ceil(BHRAMARI_INHALE * (1 - p))) };
    }
    const p = (t - exhaleAt) / (cycle - exhaleAt);
    return { hum: true, progress: p, count: Math.max(1, Math.ceil(BHRAMARI_HUM * (1 - p))) };
  }
  const tCounts = Math.min(ROUND_COUNTS, Math.max(0, (tInRound / cycle) * ROUND_COUNTS));
  if (tCounts < BHRAMARI_INHALE) {
    return { hum: false, progress: tCounts / BHRAMARI_INHALE, count: Math.max(1, Math.ceil(BHRAMARI_INHALE - tCounts)) };
  }
  const within = tCounts - BHRAMARI_INHALE;
  return { hum: true, progress: within / BHRAMARI_HUM, count: Math.max(1, Math.ceil(BHRAMARI_HUM - within)) };
}

function drawBg(ctx: CanvasRenderingContext2D) {
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = "#040f18"; ctx.fillRect(0, 0, W, H);
  STARS.forEach(([x, y, r]) => { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI*2); ctx.fillStyle = "rgba(255,255,255,0.50)"; ctx.fill(); });
  ctx.beginPath(); ctx.arc(370,34,16,0,Math.PI*2); ctx.fillStyle=glow(0.07); ctx.fill();
  ctx.beginPath(); ctx.arc(370,34,10,0,Math.PI*2); ctx.fillStyle=glow(0.13); ctx.fill();
  ctx.beginPath(); ctx.arc(370,34, 7,0,Math.PI*2); ctx.fillStyle="#f2f9ff"; ctx.fill();
}

function drawOrb(ctx: CanvasRenderingContext2D, radius: number, fill: number) {
  const haloR = radius + 70;
  const halo = ctx.createRadialGradient(cx, circCY, radius * 0.4, cx, circCY, haloR);
  halo.addColorStop(0,   rgba(+(0.16 + 0.22 * fill).toFixed(3)));
  halo.addColorStop(0.6, rgba(+(0.05 + 0.08 * fill).toFixed(3)));
  halo.addColorStop(1,   rgba(0));
  ctx.beginPath(); ctx.arc(cx, circCY, haloR, 0, Math.PI*2); ctx.fillStyle = halo; ctx.fill();
  const grd = ctx.createRadialGradient(cx - radius*0.3, circCY - radius*0.3, 2, cx, circCY, radius);
  grd.addColorStop(0,    glow(+(0.22 + 0.14 * fill).toFixed(3)));
  grd.addColorStop(0.45, rgba(+(0.28 + 0.12 * fill).toFixed(3)));
  grd.addColorStop(1,    rgba(0.05));
  ctx.beginPath(); ctx.arc(cx, circCY, radius, 0, Math.PI*2); ctx.fillStyle = grd; ctx.fill();
  ctx.strokeStyle = rgba(+(0.70 + 0.22 * fill).toFixed(3)); ctx.lineWidth = 2.2; ctx.stroke();
}

// Expanding rings; `wobble` > 0 makes them ripple like sound waves.
function drawRings(ctx: CanvasRenderingContext2D, radius: number, cosmetic: number, strength: number, wobble: number) {
  for (let i = 0; i < 6; i++) {
    const rp = ((cosmetic * 0.9 + i * 16.6) % 100) / 100;
    const rr = radius + 6 + rp * 42;
    const alpha = (1 - rp) * strength;
    ctx.beginPath();
    for (let k = 0; k <= 72; k++) {
      const th = (k / 72) * Math.PI * 2;
      const r = rr + Math.sin(th * 12 + cosmetic * 0.35 + i) * wobble * (1 - rp);
      const x = cx + Math.cos(th) * r, y = circCY + Math.sin(th) * r;
      if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = rgba(+alpha.toFixed(3)); ctx.lineWidth = 1.2; ctx.stroke();
  }
}

function drawCountArc(ctx: CanvasRenderingContext2D, progress: number, label: string) {
  const arcY = circCY + 185;
  ctx.beginPath(); ctx.arc(cx, arcY, 26, 0, Math.PI*2);
  ctx.strokeStyle = rgba(0.08); ctx.lineWidth = 2.5; ctx.stroke();
  ctx.beginPath(); ctx.arc(cx, arcY, 26, -Math.PI/2, -Math.PI/2 + progress * Math.PI*2);
  ctx.strokeStyle = accent; ctx.lineWidth = 2.5; ctx.stroke();
  ctx.textAlign = "center"; ctx.font = "bold 26px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle = rgba(0.88);
  ctx.textBaseline = "middle"; ctx.fillText(label, cx, arcY); ctx.textBaseline = "alphabetic";
}

function drawPhaseBar(ctx: CanvasRenderingContext2D, humActive: boolean | null, footer: string) {
  const tx = pick(slide10aContent).canvas;
  const barY = circCY + 230, barX = 30, barW = W - 60;
  const segs = [
    { label: tx.barInhale, counts: BHRAMARI_INHALE, cur: humActive === false },
    { label: tx.barHum,    counts: BHRAMARI_HUM,    cur: humActive === true },
  ];
  let x = barX;
  segs.forEach(s => {
    const sw = (s.counts / ROUND_COUNTS) * barW;
    ctx.fillStyle = s.cur ? rgba(0.28) : rgba(0.07); ctx.fillRect(x, barY, sw - 2, 18);
    ctx.strokeStyle = s.cur ? rgba(0.55) : rgba(0.12); ctx.lineWidth = 1; ctx.strokeRect(x, barY, sw - 2, 18);
    ctx.font = "9px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle = s.cur ? accent : rgba(0.28);
    ctx.textAlign = "center"; ctx.fillText(s.label, x + (sw - 2) / 2, barY + 12);
    x += sw;
  });
  ctx.font = "13px 'Space Grotesk',Arial,sans-serif"; ctx.fillStyle = rgba(0.38);
  ctx.textAlign = "center"; ctx.fillText(footer, cx, barY + 34);
}

function drawTitle(ctx: CanvasRenderingContext2D, title: string, big: string) {
  ctx.textAlign = "center";
  ctx.font = "bold 17px 'JetBrains Mono','Courier New',monospace";
  // Longer (German) labels shrink to stay inside the smallest orb; English is unaffected.
  const tw = ctx.measureText(title).width;
  if (tw > 64) ctx.font = `bold ${Math.floor(17 * 64 / tw)}px 'JetBrains Mono','Courier New',monospace`;
  ctx.fillStyle = accent; ctx.fillText(title, cx, circCY - 12);
  ctx.font = "bold 44px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle = rgba(0.90);
  ctx.textBaseline = "middle"; ctx.fillText(big, cx, circCY + 14); ctx.textBaseline = "alphabetic";
}

function drawView(ctx: CanvasRenderingContext2D, v: View) {
  const tx = pick(slide10aContent).canvas; // slides remount on language change
  drawBg(ctx);
  if (v.stage === "practice" || v.stage === "idle") {
    const p = v.stage === "practice" ? v : null;
    const { hum, progress, count } = phaseAt(p ? p.tInRound : 0, p ? p.cycle : TIMER_CYCLE, p?.exhaleAt);
    const radius = hum
      ? R_MAX - (R_MAX - R_MIN) * easeInOut(progress)
      : R_MIN + (R_MAX - R_MIN) * easeInOut(progress);
    const fill = (radius - R_MIN) / (R_MAX - R_MIN);
    const cosmetic = p ? p.cosmetic : 0;
    drawRings(ctx, radius, cosmetic, hum ? 0.30 : 0.12 + 0.12 * fill, hum ? 2.6 : 0);
    drawOrb(ctx, radius, fill);
    drawTitle(ctx, hum ? tx.hum : tx.inhale, String(count));
    drawCountArc(ctx, progress, String(count));
    drawPhaseBar(ctx, hum, p ? tx.round(p.round + 1, BHRAMARI_ROUNDS) : tx.idleFooter);
    if (v.stage === "idle") {
      ctx.fillStyle = "rgba(4,15,24,0.40)"; ctx.fillRect(0, 0, W, H);
      ctx.textAlign = "center"; ctx.font = "14px 'Space Grotesk',Arial,sans-serif";
      ctx.fillStyle = rgba(0.45); ctx.fillText(tx.pressPlay, cx, H - 8);
    }
    return;
  }
  if (v.stage === "intro") {
    const breathe = 0.5 + 0.5 * Math.sin(v.cosmetic * 0.03);
    const radius = R_MIN + 8 * breathe;
    drawRings(ctx, radius, v.cosmetic * 0.5, 0.10, 0);
    drawOrb(ctx, radius, 0.2 * breathe);
    drawCountArc(ctx, 0, "");
    drawPhaseBar(ctx, null, tx.introFooter);
  }
}

// ─── Web Audio: background drone + fallback cues ──────────────────────────────
// One context per session. The drone plays for the whole practice (same sound
// as the other pranayama slides); the chime and guide-hum are only scheduled
// when the narration track is unavailable.
type Cues = { ctx: AudioContext; hum: GainNode; drone: GainNode };

const DRONE_LEVEL = 0.04;

function createCues(): Cues | null {
  const AC = window.AudioContext || (window as any).webkitAudioContext;
  if (!AC) return null;
  const ctx: AudioContext = new AC();

  // Background drone: C#3 · G#3 · C#4 sines through a soft feedback echo.
  const drone = ctx.createGain();
  drone.gain.setValueAtTime(0, ctx.currentTime);
  drone.gain.linearRampToValueAtTime(DRONE_LEVEL, ctx.currentTime + 2.0);
  drone.connect(ctx.destination);
  const delay = ctx.createDelay(1.0); const fb = ctx.createGain();
  delay.delayTime.value = 0.45; fb.gain.value = 0.35;
  delay.connect(fb); fb.connect(delay); delay.connect(drone);
  ([[138.6, 0.55], [207.65, 0.28], [277.2, 0.14]] as [number, number][]).forEach(([f, v]) => {
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = "sine"; o.frequency.value = f; g.gain.value = v;
    o.connect(g); g.connect(delay); g.connect(drone); o.start();
  });

  const hum = ctx.createGain();
  hum.gain.value = 0;
  hum.connect(ctx.destination);
  // A low, soft "mmm" to hum along with, in tune with the drone: C#3 + octave.
  ([[138.59, 1], [277.18, 0.25]] as [number, number][]).forEach(([f, v]) => {
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = "sine"; o.frequency.value = f; g.gain.value = v;
    o.connect(g); g.connect(hum); o.start();
  });
  return { ctx, hum, drone };
}

function chime(c: Cues, at: number) {
  const o = c.ctx.createOscillator(); const g = c.ctx.createGain();
  o.type = "sine"; o.frequency.value = 659.25;
  g.gain.setValueAtTime(0, at);
  g.gain.linearRampToValueAtTime(0.05, at + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, at + 1.4);
  o.connect(g); g.connect(c.ctx.destination);
  o.start(at); o.stop(at + 1.5);
}

// Schedule every round's cues on the audio clock up front, so pacing stays
// exact even when the tab is in the background and rAF is throttled.
function scheduleRounds(c: Cues) {
  const t0 = c.ctx.currentTime + 0.05;
  for (let r = 0; r < BHRAMARI_ROUNDS; r++) {
    const start = t0 + r * TIMER_CYCLE;
    const humOn = start + BHRAMARI_INHALE * COUNT_SECS;
    const humOff = start + TIMER_CYCLE;
    chime(c, start);
    c.hum.gain.setValueAtTime(0, humOn);
    c.hum.gain.linearRampToValueAtTime(0.045, humOn + 0.4);
    c.hum.gain.setValueAtTime(0.045, humOff - 0.6);
    c.hum.gain.linearRampToValueAtTime(0, humOff);
  }
}

// Fade out and close a specific context. Takes the Cues object it should
// close, so a quick stop → start can never close the new session's context.
function closeCues(c: Cues | null, withChime = false) {
  if (!c) return;
  const now = c.ctx.currentTime;
  try {
    c.hum.gain.cancelScheduledValues(now);
    c.hum.gain.setValueAtTime(c.hum.gain.value, now);
    c.hum.gain.linearRampToValueAtTime(0, now + 0.3);
    c.drone.gain.cancelScheduledValues(now);
    c.drone.gain.setValueAtTime(c.drone.gain.value, now);
    c.drone.gain.linearRampToValueAtTime(0, now + 1.2);
    if (withChime) chime(c, now + 0.05);
  } catch (_) {}
  window.setTimeout(() => { c.ctx.close().catch(() => {}); }, withChime ? 1700 : 1400);
}

// ─── Bhramari Guide (button + canvas) ─────────────────────────────────────────
const BhramariGuide: React.FC = () => {
  const { meta } = useSlide10a();
  const [playing, setPlaying] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Mutable session state. `token` increments on every start/stop; any
  // timer or audio callback from an older session sees a mismatch and bails.
  const st = useRef({
    stage: "idle" as Stage,
    token: 0,
    timers: [] as number[],
    animId: 0,
    round: 0,
    lastT: 0,
    cycle: TIMER_CYCLE,
    exhaleAt: null as number | null,
    narrationOk: null as boolean | null, // null = still unknown
    useNarration: false,
    timerMode: false,
    practiceT0: 0,
    intro: null as HTMLAudioElement | null,
    practice: null as HTMLAudioElement | null,
    // Practice track routed through Web Audio so it can be faded on iOS too
    // (iOS ignores <audio>.volume). Created once, inside the first Play tap.
    fadeCtx: null as AudioContext | null,
    fadeGain: null as GainNode | null,
    cues: null as Cues | null,
    ctx2d: null as CanvasRenderingContext2D | null,
    // Callbacks set up once on mount (see effect below).
    start: () => {},
    stop: () => {},
  });

  useEffect(() => {
    const s = st.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d") ?? null;
    if (canvas && ctx) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = W * dpr; canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    s.ctx2d = ctx;

    const intro = localAudio(INTRO_SRC);       intro.preload = "auto";
    const practice = localAudio(PRACTICE_SRC); practice.preload = "auto";
    s.intro = intro; s.practice = practice;

    const later = (fn: () => void, ms: number) => { s.timers.push(window.setTimeout(fn, ms)); };
    const clearTimers = () => { s.timers.forEach(id => clearTimeout(id)); s.timers = []; };

    const currentView = (): View => {
      const cosmetic = performance.now() / 16;
      if (s.stage === "intro") return { stage: "intro", cosmetic };
      if (s.stage === "practice") {
        if (s.useNarration) {
          return { stage: "practice", cosmetic, tInRound: practice.currentTime, cycle: s.cycle, round: s.round, exhaleAt: s.exhaleAt };
        }
        const elapsed = (performance.now() - s.practiceT0) / 1000;
        const round = Math.min(Math.floor(elapsed / TIMER_CYCLE), BHRAMARI_ROUNDS - 1);
        return { stage: "practice", cosmetic, tInRound: elapsed - round * TIMER_CYCLE, cycle: TIMER_CYCLE, round };
      }
      return { stage: "idle" };
    };

    const draw = () => { if (s.ctx2d) drawView(s.ctx2d, currentView()); };

    // Fade the hum out over the last HUM_FADE_SECS of each round; full level
    // otherwise (the next round starts with a short silence, then "Inhale").
    const applyFade = () => {
      const left = s.stage === "practice" && s.useNarration ? s.cycle - practice.currentTime : Infinity;
      const level = left < HUM_FADE_SECS ? Math.max(0, left / HUM_FADE_SECS) : 1;
      if (s.fadeGain && s.fadeCtx) s.fadeGain.gain.setTargetAtTime(level, s.fadeCtx.currentTime, 0.015);
      else { try { practice.volume = level; } catch (_) {} }
    };
    const ensureFadeRoute = () => {
      if (s.fadeCtx) { s.fadeCtx.resume().catch(() => {}); return; }
      const AC = window.AudioContext ?? (window as any).webkitAudioContext;
      if (!AC) return;
      try {
        const ac: AudioContext = new AC();
        const gain = ac.createGain();
        ac.createMediaElementSource(practice).connect(gain);
        gain.connect(ac.destination);
        s.fadeCtx = ac; s.fadeGain = gain;
      } catch (_) { /* fall back to element volume */ }
    };

    const loop = () => {
      cancelAnimationFrame(s.animId);
      const tick = () => {
        draw();
        applyFade();
        if (s.stage !== "idle") s.animId = requestAnimationFrame(tick);
      };
      tick();
    };

    const resetAudio = (a: HTMLAudioElement) => {
      a.onended = null;
      try { a.pause(); a.currentTime = 0; } catch (_) {}
    };

    const complete = (token: number) => {
      if (s.token !== token || s.stage !== "practice") return;
      s.token++;
      clearTimers();
      resetAudio(intro);
      resetAudio(practice);
      closeCues(s.cues, true);
      s.cues = null;
      s.stage = "idle";
      setPlaying(false);
      loop();
      // Natural finish (not user-stop) → advance to the next slide (Meditation).
      window.dispatchEvent(new Event("yogyism:next"));
    };

    // Self-paced guide with Web Audio cues. Guarded so the play() rejection
    // and the "error" event (which can both fire) only start it once.
    const startTimerMode = (token: number) => {
      if (s.token !== token || s.stage !== "practice" || s.timerMode) return;
      s.timerMode = true;
      s.useNarration = false;
      resetAudio(practice);
      s.practiceT0 = performance.now();
      if (s.cues) scheduleRounds(s.cues);
      later(() => complete(token), BHRAMARI_ROUNDS * TIMER_CYCLE * 1000);
    };

    const beginPractice = (token: number) => {
      if (s.token !== token || s.stage !== "intro") return;
      clearTimers();
      resetAudio(intro);
      s.stage = "practice";
      s.round = 0;
      s.lastT = 0;
      s.timerMode = false;

      if (s.narrationOk === false) { startTimerMode(token); return; }
      s.useNarration = true;
      practice.currentTime = 0;
      practice.loop = BHRAMARI_ROUNDS > 1;
      practice.play().catch(() => startTimerMode(token));
    };

    // Narration track: one round, looped; count rounds on wrap-around and let
    // the final round end naturally (loop off) so nothing of a 6th round plays.
    practice.addEventListener("loadedmetadata", () => {
      if (isFinite(practice.duration) && practice.duration > 0) { s.cycle = practice.duration; s.narrationOk = true; }
      // Measured "Exhale" moment of whichever recording is playing (English
      // when there is no German file yet).
      s.exhaleAt = BHRAMARI_EXHALE_AT[audioUrl(PRACTICE_SRC) === PRACTICE_SRC ? "en" : getLang()];
    });
    practice.addEventListener("error", () => {
      s.narrationOk = false;
      // Failed during practice: fall through to the self-paced guide.
      if (s.stage === "practice") startTimerMode(s.token);
    });
    practice.addEventListener("timeupdate", () => {
      applyFade(); // also runs while animation frames are paused (screen off)
      if (s.stage !== "practice" || !s.useNarration) return;
      const t = practice.currentTime;
      if (t + 0.25 < s.lastT) {
        s.round++;
        if (s.round >= BHRAMARI_ROUNDS - 1) practice.loop = false;
      }
      s.lastT = t;
    });
    practice.addEventListener("ended", () => {
      if (s.stage === "practice" && s.useNarration) complete(s.token);
    });

    // Must be called from the click handler (user gesture) — see start below.
    s.start = () => {
      const token = ++s.token;
      clearTimers();
      s.stage = "intro";

      // iOS only lets an <audio> element play later if it was started inside
      // a user gesture, so touch the practice track now (muted, paused at once).
      if (s.narrationOk !== false) {
        practice.muted = true;
        practice.play().catch(() => {});
        practice.pause();
        practice.currentTime = 0;
        practice.muted = false;
      }
      ensureFadeRoute(); // inside the gesture, like the cues below
      applyFade();
      // Created inside the gesture so it starts in the "running" state.
      closeCues(s.cues);
      s.cues = createCues();

      const proceed = () => later(() => beginPractice(token), 1000);
      intro.onended = () => { intro.onended = null; if (s.token === token) proceed(); };
      intro.currentTime = 0;
      intro.play().then(() => {
        // Safety net if "ended" never fires (stalled stream). beginPractice is
        // idempotent per token, so a late duplicate call does nothing.
        const ms = isFinite(intro.duration) ? Math.ceil(intro.duration * 1000) + 3000 : 45000;
        if (s.token === token) later(() => beginPractice(token), ms);
      }).catch(() => {
        // No intro recording (or blocked) — go straight to the practice.
        if (s.token === token) later(() => beginPractice(token), 600);
      });

      loop();
    };

    s.stop = () => {
      s.token++;
      clearTimers();
      resetAudio(intro);
      resetAudio(practice);
      closeCues(s.cues);
      s.cues = null;
      s.stage = "idle";
      cancelAnimationFrame(s.animId);
      draw();
    };

    draw();

    return () => {
      s.token++;
      clearTimers();
      cancelAnimationFrame(s.animId);
      s.fadeCtx?.close().catch(() => {});
      s.fadeCtx = null; s.fadeGain = null;
      [intro, practice].forEach(a => {
        resetAudio(a);
        a.removeAttribute("src");
        a.load();
      });
      if (s.cues) { s.cues.ctx.close().catch(() => {}); s.cues = null; }
      s.stage = "idle";
    };
  }, []);

  // Slide changes (and other guides starting) broadcast yogyism:stop.
  useEffect(() => {
    const onStop = () => { st.current.stop(); setPlaying(false); };
    window.addEventListener("yogyism:stop", onStop);
    return () => window.removeEventListener("yogyism:stop", onStop);
  }, []);

  const handleToggle = () => {
    if (playing) {
      st.current.stop();
      setPlaying(false);
    } else {
      st.current.start();
      setPlaying(true);
      window.dispatchEvent(new Event("yogyism:play")); // ← pause slideshow
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", height: "100%" }}>
      <div style={{ flexShrink: 0, marginBottom: "clamp(4px,0.5vw,6px)" }}>
        <button
          onClick={handleToggle}
          aria-label={playing ? meta.ariaStop : meta.ariaStart}
          style={{
            width: 32, height: 32,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: playing ? rgba(0.08) : rgba(0.32),
            border: `1px solid ${rgba(playing ? 0.32 : 0.85)}`,
            borderRadius: "50%", cursor: "pointer",
            color: playing ? rgba(0.60) : accent,
            transition: "all 0.2s",
            animation: playing ? "none" : "yogyismPulse_bhramari 1.3s ease-in-out infinite",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = rgba(0.28))}
          onMouseLeave={e => (e.currentTarget.style.background = playing ? rgba(0.08) : rgba(0.20))}
        >
          {playing ? (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
              <rect x="0" y="0" width="10" height="10" rx="1"/>
            </svg>
          ) : (
            <svg width="9" height="10" viewBox="0 0 9 10" fill="currentColor" aria-hidden="true">
              <polygon points="0,0 9,5 0,10"/>
            </svg>
          )}
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0, width: "100%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={meta.ariaGuide}
          style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>
    </div>
  );
};

// ─── Slide content (authored at the ScaleToFit design size) ────────────────────
const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</span>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide10aInner: React.FC = () => {
  const { meta: slide10aMeta, steps: slide10aSteps, what: slide10aWhat, duration: slide10aDuration } = useSlide10a();
  // Load the font once; never removed, other slides share it.
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const link = document.createElement("link");
      link.rel  = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const lbl: CSSProperties = {
    fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO,
    color: accent, letterSpacing: 3, textTransform: "uppercase",
    marginBottom: "clamp(4px,0.55vw,8px)", fontStyle: "normal",
  };
  const txt: CSSProperties = {
    fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontFamily: FONT_BODY,
    lineHeight: 1.7, color: "#cde0f2", margin: 0,
  };

  const rootRef = useRef<HTMLDivElement>(null);

  return (
    <div style={S.root} ref={rootRef}>
      <SideStarField rootRef={rootRef} />

      {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
      <div style={S.subtitleBar}>
        <div style={{ fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc", fontFamily: FONT_BODY }}>
          {slide10aMeta.breadcrumbPre} &nbsp;·&nbsp;{" "}
          <span style={{ color: accent }}>{slide10aMeta.breadcrumbAccent}</span>
        </div>
        <div style={{
          fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
          color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
          whiteSpace: "nowrap",
        }}>
          {slide10aMeta.headerTitlePre}<span style={{ color: accent }}>{slide10aMeta.headerTitleAccent}</span>
        </div>
      </div>

      <div style={S.body}>
        <div style={S.content}>
          <div>
            <div style={{ fontSize: "clamp(8px,0.68vw,11px)", fontFamily: FONT_MONO, color: rgba(0.55), letterSpacing: 3, marginBottom: "clamp(4px,0.5vw,7px)" }}>
              {slide10aMeta.sanskrit}
            </div>
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(19px,2.3vw,30px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", margin: "0 0 clamp(2px,0.35vw,6px)", lineHeight: 1.2 }}>
              {slide10aMeta.titlePre}<span style={{ color: accent }}>{slide10aMeta.titleAccentWord}</span>{slide10aMeta.titlePost}
            </h1>
            <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.1vw,15px)", fontStyle: "normal", fontWeight: 300, color: "#7aa8cc", margin: 0, letterSpacing: 0.3 }}>
              {slide10aMeta.subtitle}
            </p>
          </div>

          <div style={{ borderBottom: `1px solid ${rgba(0.10)}`, paddingBottom: "clamp(6px,0.85vw,11px)" }}>
            <div style={lbl}>{slide10aMeta.whatLabel}</div>
            <p style={txt}>{renderSegs(slide10aWhat, tint.bold)}</p>
          </div>

          <div style={{ borderBottom: `1px solid ${rgba(0.10)}`, paddingBottom: "clamp(6px,0.85vw,11px)" }}>
            <div style={lbl}>{slide10aMeta.howLabel}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "clamp(5px,0.65vw,8px)" }}>
              {slide10aSteps.map((item, i) => (
                <div key={i} style={{ display: "flex", gap: "clamp(8px,1vw,14px)", alignItems: "flex-start" }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: accent, flexShrink: 0, marginTop: 6, opacity: 0.7 }} />
                  <div>
                    <span style={{ fontFamily: FONT_BODY, fontSize: "clamp(11px,0.92vw,14px)", fontStyle: "normal", fontWeight: 700, color: tint.step }}>{item.step} &nbsp;</span>
                    <span style={{ fontFamily: FONT_BODY, fontSize: "clamp(11px,0.92vw,14px)", fontStyle: "normal", fontWeight: 300, color: "#9bbdd6" }}>{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: rgba(0.06), border: `1px solid ${rgba(0.22)}`, borderRadius: 8, padding: "clamp(11px,1.3vw,18px) clamp(13px,1.5vw,20px)" }}>
            <div style={lbl}>{slide10aMeta.durationLabel}</div>
            <p style={{ ...txt, color: tint.calloutText }}>{renderSegs(slide10aDuration, tint.calloutBold)}</p>
          </div>
        </div>

        {/* Panel — humming guide fills the full panel height */}
        <div style={S.panel}>
          <BhramariGuide />
        </div>
      </div>
    </div>
  );
};

const HTLDSlide10aGuideMobile: React.FC = () => {
  const { meta } = useSlide10a();
  return (
  <div style={{ background:"#040f18", color:"#d0e8f8", fontFamily:font, height:"100%", width:"100%", boxSizing:"border-box", display:"flex", flexDirection:"column", padding:"18px 16px 20px", overflow:"hidden" }}>
    <div style={{ flexShrink:0, textAlign:"center", marginBottom:10 }}>
      <div style={{ fontFamily:FONT_MONO, fontSize:10, letterSpacing:2, color:rgba(0.7), textTransform:"uppercase", marginBottom:4 }}>{meta.breadcrumbPre}</div>
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle:"normal", fontWeight:600, fontSize:22, margin:0, color:"#e8f4ff" }}>{meta.headerTitlePre}<span style={{color:accent}}>{meta.headerTitleAccent}</span></h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle:"normal", fontWeight:300, fontSize:12.5, color:"#7aa8cc", margin:"3px 0 0" }}>{meta.guideSubtitle}</p>
    </div>
    <div style={{ flex:1, minHeight:0, width:"100%", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
      <BhramariGuide />
    </div>
  </div>
  );
};

// Tutorial slide: scaled desktop layout in landscape; in portrait, readable
// text in All mode and the interactive guide in Practice mode.
const HTLDSlide10a: React.FC = () => {
  const portrait = useIsPortrait();
  const mode = useMode();
  if (!portrait) return <ScaleToFit><HTLDSlide10aInner /></ScaleToFit>;
  return mode === "practice" ? <HTLDSlide10aGuideMobile /> : <HTLDSlide10aMobile />;
};

// PracticeSwiper (/session) version — always the practice layout.
export const BhramariBody: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide10aGuideMobile />
    : <ScaleToFit><HTLDSlide10aInner /></ScaleToFit>;
};

export default HTLDSlide10a;
